import type { Active, Announcements, Over, ScreenReaderInstructions } from '@dnd-kit/core';

type Translate = (key: string, options?: Record<string, unknown>) => string;

// Forma mínima de los objetos de react-beautiful-dnd: el paquete no trae tipos y no se importa en código nuevo (boy-scout.md).
type RbdLocation = { droppableId: string; index: number };
export type RbdDragStart = { source: RbdLocation };
export type RbdDragUpdate = RbdDragStart & { destination?: RbdLocation | null };
export type RbdDropResult = RbdDragUpdate & { reason?: 'CANCEL' | 'DROP' };
export type RbdResponderProvided = { announce: (message: string) => void };

export type DndKitAccessibility = {
  announcements: Announcements;
  screenReaderInstructions: ScreenReaderInstructions;
};

const NS = 'drag-and-drop';

// Las librerías dan el índice desde 0; el lector dice la posición desde 1.
function toPosition(index: number): number {
  return index + 1;
}

/** Instrucciones del asa para `DragDropContext dragHandleUsageInstructions`. */
export function getRbdDragHandleInstructions(t: Translate): string {
  return t(`${NS}:Keyboard instructions space`);
}

export function getRbdDragStartMessage(t: Translate, { source }: RbdDragStart): string {
  return t(`${NS}:Picked up item in position X`, { position: toPosition(source.index) });
}

export function getRbdDragUpdateMessage(t: Translate, { destination, source }: RbdDragUpdate): string {
  if (!destination) return t(`${NS}:Item is not over a drop area`);
  const position = toPosition(destination.index);
  return destination.droppableId === source.droppableId
    ? t(`${NS}:Item is over position X`, { position })
    : t(`${NS}:Item is over position X of another list`, { position });
}

export function getRbdDragEndMessage(t: Translate, { destination, reason, source }: RbdDropResult): string {
  if (reason === 'CANCEL') return t(`${NS}:Movement cancelled`);
  if (!destination) return t(`${NS}:Dropped item outside a drop area`);
  const position = toPosition(destination.index);
  return destination.droppableId === source.droppableId
    ? t(`${NS}:Dropped item in position X`, { position })
    : t(`${NS}:Dropped item in position X of another list`, { position });
}

type RbdResponder<Event> = (event: Event, provided: RbdResponderProvided) => void;

type RbdResponders<Start, Update, Drop> = {
  onDragEnd: RbdResponder<Drop>;
  onDragStart: RbdResponder<Start>;
  onDragUpdate: RbdResponder<Update>;
};

/**
 * Responders de `DragDropContext` que anuncian en el idioma de la UI y después llaman a los de la vista.
 * Con `announce`, react-beautiful-dnd no lee su anuncio por defecto, que está en inglés (TAN-10927).
 */
export function getRbdResponders<Start extends RbdDragStart, Update extends RbdDragUpdate, Drop extends RbdDropResult>(
  t: Translate,
  own: Partial<RbdResponders<Start, Update, Drop>> = {}
): RbdResponders<Start, Update, Drop> {
  return {
    onDragStart(start, provided) {
      provided.announce(getRbdDragStartMessage(t, start));
      own.onDragStart?.(start, provided);
    },
    onDragUpdate(update, provided) {
      provided.announce(getRbdDragUpdateMessage(t, update));
      own.onDragUpdate?.(update, provided);
    },
    onDragEnd(result, provided) {
      provided.announce(getRbdDragEndMessage(t, result));
      own.onDragEnd?.(result, provided);
    },
  };
}

// Solo los elementos de un `SortableContext` traen índice; una zona de `useDroppable` no.
function getSortableIndex(node: Active | Over | null): number | null {
  const index = node?.data?.current?.sortable?.index;
  return typeof index === 'number' ? index : null;
}

// Un submódulo puede soltarse en otro módulo: es otra lista si cambia el contenedor del `SortableContext`.
function isOtherList(active: Active, over: Over): boolean {
  const from = active.data?.current?.sortable?.containerId;
  const to = over.data?.current?.sortable?.containerId;
  return from !== undefined && to !== undefined && from !== to;
}

export type CanDrop = (active: Active, over: Over) => boolean;

function allowAnyDrop(): boolean {
  return true;
}

/**
 * Valor para la prop `accessibility` de `DndContext`: sin él, dnd-kit habla en inglés y nombra los elementos por su id.
 * `canDrop` repite las reglas del `onDragEnd` de la vista: dnd-kit deja pasar por encima de destinos que luego se descartan.
 */
export function getDndKitAccessibility(t: Translate, canDrop: CanDrop = allowAnyDrop): DndKitAccessibility {
  return {
    announcements: {
      onDragStart({ active }) {
        const index = getSortableIndex(active);
        return index === null ? t(`${NS}:Picked up item`) : t(`${NS}:Picked up item in position X`, { position: toPosition(index) });
      },
      onDragOver({ active, over }) {
        if (!over || !canDrop(active, over)) return t(`${NS}:Item is not over a drop area`);
        const index = getSortableIndex(over);
        if (index === null) return t(`${NS}:Item is over another drop area`);
        const position = toPosition(index);
        return isOtherList(active, over) ? t(`${NS}:Item is over position X of another list`, { position }) : t(`${NS}:Item is over position X`, { position });
      },
      onDragEnd({ active, over }) {
        if (!over || !canDrop(active, over)) return t(`${NS}:Dropped item outside a drop area`);
        const index = getSortableIndex(over);
        if (index === null) return t(`${NS}:Dropped item`);
        const position = toPosition(index);
        return isOtherList(active, over)
          ? t(`${NS}:Dropped item in position X of another list`, { position })
          : t(`${NS}:Dropped item in position X`, { position });
      },
      onDragCancel() {
        return t(`${NS}:Movement cancelled`);
      },
    },
    screenReaderInstructions: { draggable: t(`${NS}:Keyboard instructions space or enter`) },
  };
}

/** `roleDescription` de `useSortable`: por defecto dnd-kit pone «sortable». */
export function getSortableRoleDescription(t: Translate): string {
  return t(`${NS}:sortable`);
}

/** `roleDescription` de `useDraggable`: por defecto dnd-kit pone «draggable». */
export function getDraggableRoleDescription(t: Translate): string {
  return t(`${NS}:draggable`);
}
